// Mesajlar › Yeni kişiler — sağ bölme: kişi başlığı (+ "Hastaya bağla"), mesaj akışı, yanıt.

import React, { useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { Box, Button, CircularProgress, IconButton, Skeleton, Typography } from "@mui/material";
import {
  ArrowBackRounded as BackIcon,
  LinkRounded as LinkIcon,
  WhatsApp as WhatsAppIcon,
} from "@mui/icons-material";
import { ActionButton, EmptyState, formatPhoneDisplay } from "../../components/atoms";
import { COLORS, TYPO } from "../../styles/uiConstants";
import { buildTimeline } from "./messageFormat";
import MessageBubble from "./MessageBubble";
import MessageComposer from "./MessageComposer";
import { ContactAvatar, contactName } from "./ContactList";
import useTerms from "../../hooks/useTerms";

const CONTACT_WINDOW_NOTE = "Bu kişi son 24 saatte WhatsApp'tan yazmadığı için serbest mesaj gönderilemez.";

function ThreadSkeleton() {
  return (
    <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 1.5 }}>
      {[55, 40, 65].map((w, i) => (
        <Box key={i} sx={{ display: "flex", justifyContent: i % 2 ? "flex-end" : "flex-start" }}>
          <Skeleton variant="rounded" width={`${w}%`} height={44} sx={{ borderRadius: 2.5 }} />
        </Box>
      ))}
    </Box>
  );
}

/**
 * @param {object|null} thread - { contact, messages, hasMore, windowOpen, windowExpiresAt }
 * @param {boolean} loading
 * @param {boolean} error
 * @param {object[]} olderMessages
 * @param {boolean} hasMoreOlder
 * @param {boolean} loadingOlder
 * @param {() => void} onLoadOlder
 * @param {(text: string) => Promise<boolean>} onSend
 * @param {boolean} sending
 * @param {() => void} onLink - "Hastaya bağla" diyaloğunu açar
 * @param {() => void} [onBack]
 * @param {object} palette
 */
export default function ContactThread({
  thread,
  loading,
  error,
  olderMessages,
  hasMoreOlder,
  loadingOlder,
  onLoadOlder,
  onSend,
  sending,
  onLink,
  onBack,
  palette,
}) {
  const terms = useTerms();
  const scrollerRef = useRef(null);
  const lastKeyRef = useRef(null);
  const prevHeightRef = useRef(null);

  const timeline = useMemo(
    () => buildTimeline([olderMessages || [], thread?.messages || []]),
    [olderMessages, thread?.messages]
  );
  const newestKey = timeline.length ? timeline[timeline.length - 1].key : null;
  const oldestKey = timeline.length ? timeline[0].key : null;
  const contact = thread?.contact;
  const contactKey = contact?._id || null;

  useLayoutEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    if (newestKey && newestKey !== lastKeyRef.current) {
      el.scrollTop = el.scrollHeight;
      lastKeyRef.current = newestKey;
    } else if (prevHeightRef.current != null) {
      el.scrollTop = el.scrollHeight - prevHeightRef.current;
    }
    prevHeightRef.current = null;
  }, [newestKey, oldestKey]);

  useEffect(() => {
    lastKeyRef.current = null;
  }, [contactKey]);

  if (loading && !thread) return <ThreadSkeleton />;
  if (error && !thread) {
    return (
      <EmptyState
        variant="illustrated"
        icon={<WhatsAppIcon />}
        title="Konuşma açılamadı"
        description="Bu kişi artık listede olmayabilir; başka bir hastaya bağlanmış olabilir."
        sx={{ flex: 1 }}
      />
    );
  }
  if (!thread) return null;

  const windowClosed = thread.windowOpen === false;
  const phone = formatPhoneDisplay(contact?.phone) || contact?.phone || "";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: { xs: 1.5, md: 3 },
          py: 1.5,
          borderBottom: `1px solid ${COLORS.slate200}`,
          flexShrink: 0,
        }}
      >
        {onBack ? (
          <IconButton size="small" aria-label="Kişilere dön" onClick={onBack}>
            <BackIcon sx={{ fontSize: 20 }} />
          </IconButton>
        ) : null}
        <ContactAvatar size={36} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography noWrap sx={{ fontSize: TYPO.title, fontWeight: 600, color: COLORS.slate800 }}>
            {contactName(contact)}
          </Typography>
          {contact?.profileName && phone ? (
            <Typography noWrap sx={{ fontSize: TYPO.caption, color: COLORS.slate500, fontVariantNumeric: "tabular-nums" }}>
              {phone}
            </Typography>
          ) : null}
        </Box>
        <ActionButton
          variant="add"
          tone="soft"
          compact
          palette={palette}
          icon={<LinkIcon sx={{ fontSize: 15 }} />}
          label={`${terms.patientDat} bağla`}
          onClick={onLink}
          sx={{ flexShrink: 0 }}
        />
      </Box>

      <Box
        ref={scrollerRef}
        sx={{ flex: 1, minHeight: 0, overflowY: "auto", overscrollBehavior: "contain", px: { xs: 2, md: 3 }, py: 2 }}
      >
        {hasMoreOlder ? (
          <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
            <Button
              size="small"
              onClick={() => {
                prevHeightRef.current = scrollerRef.current?.scrollHeight ?? null;
                onLoadOlder();
              }}
              disabled={loadingOlder}
              startIcon={loadingOlder ? <CircularProgress size={12} color="inherit" /> : null}
              sx={{ textTransform: "none", fontSize: TYPO.label, color: COLORS.slate500, px: 1.5 }}
            >
              Daha eski mesajlar
            </Button>
          </Box>
        ) : null}
        {timeline.length === 0 ? (
          <EmptyState variant="illustrated-sm" icon={<WhatsAppIcon />} title="Henüz mesaj yok" />
        ) : (
          timeline.map((item) =>
            item.type === "day" ? (
              <Box key={item.key} sx={{ display: "flex", justifyContent: "center", my: 1.5 }}>
                <Typography sx={{ fontSize: TYPO.caption, color: COLORS.slate500, px: 1.25, py: 0.25, borderRadius: 10, bgcolor: COLORS.slate100 }}>
                  {item.label}
                </Typography>
              </Box>
            ) : (
              <MessageBubble key={item.key} message={item.message} palette={palette} showChannel={false} />
            )
          )
        )}
      </Box>

      <MessageComposer
        palette={palette}
        resetKey={contactKey || ""}
        sending={sending}
        disabled={windowClosed}
        note={windowClosed ? CONTACT_WINDOW_NOTE : undefined}
        onSend={onSend}
      />
    </Box>
  );
}
